import get from 'lodash.get';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { Link } from 'dibs-elements/exports/Link';
import styles from './styles/CheckoutPhoneLink.scss';
import classnames from 'classnames';

const CheckoutPhoneLink = ({ viewer, isContrastLink, hasTradeStatus, relay, children }) => {
    useEffect(() => {
        relay.refetch(
            // do not default `hasTradeStatus=false` since `null` could be
            // passed in so shorthand object notation could still cause issues
            { hasTradeStatus: !!hasTradeStatus },
            null,
            error => {
                if (window.newrelic && error) {
                    window.newrelic.noticeError(error);
                }
            },
            { force: true }
        );
    }, [hasTradeStatus, relay]);

    const phoneNumber = hasTradeStatus
        ? get(viewer, 'contactInfo.phoneNumbers.tradeSupport')
        : get(viewer, 'contactInfo.phoneNumbers.intlSupport');
    const phoneNumberLink = hasTradeStatus
        ? get(viewer, 'contactInfo.phoneNumbers.defaultTradeSupport')
        : get(viewer, 'contactInfo.phoneNumbers.defaultSupport');
    const linkStyles = classnames(styles.link, { [styles.contrastLink]: isContrastLink });
    const phoneStyles = classnames(styles.phone, { [styles.contrastLink]: isContrastLink });

    if (children && typeof children === 'function') {
        return children({ phoneNumberLink });
    }

    return (
        <span>
            <Link className={linkStyles} href={`tel:${phoneNumberLink}`}>
                {phoneNumber}
            </Link>
            <span className={phoneStyles}>{phoneNumber}</span>
        </span>
    );
};

CheckoutPhoneLink.propTypes = {
    viewer: PropTypes.object.isRequired,
    isContrastLink: PropTypes.bool,
    hasTradeStatus: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    children: PropTypes.func,
};

export default createRefetchContainer(
    CheckoutPhoneLink,
    {
        viewer: graphql`
            fragment CheckoutPhoneLink_viewer on Viewer {
                contactInfo {
                    phoneNumbers {
                        intlSupport: support(formatType: INTERNATIONAL) @skip(if: $hasTradeStatus)
                        defaultSupport: support @skip(if: $hasTradeStatus)
                        tradeSupport: tradeSupport(formatType: INTERNATIONAL)
                            @include(if: $hasTradeStatus)
                        defaultTradeSupport: tradeSupport @include(if: $hasTradeStatus)
                    }
                }
            }
        `,
    },
    graphql`
        query CheckoutPhoneLinkRefetchQuery($hasTradeStatus: Boolean!) {
            viewer {
                ...CheckoutPhoneLink_viewer
            }
        }
    `
);
